import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Search, LogOut, Crown, Filter } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  'All',
  'Work & Productivity',
  'Visual Studio',
  'Content & Social Media',
  'Business & Marketing',
  'Life & Personal Growth',
  'Coding & Technical',
  'Daily Trends (Featured)'
];

export default function Dashboard() {
  const [user, setUser] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [filteredPrompts, setFilteredPrompts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserAndPrompts();
  }, []);

  useEffect(() => {
    filterPrompts();
  }, [searchTerm, selectedCategory, prompts]);

  const fetchUserAndPrompts = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const userResponse = await axios.get(`${BACKEND_URL}/api/user/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(userResponse.data);

      if (userResponse.data.subscription_status !== 'active' && userResponse.data.role !== 'admin') {
        toast.error('Please subscribe to access prompts');
        navigate('/pricing');
        return;
      }

      const promptsResponse = await axios.get(`${BACKEND_URL}/api/prompts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrompts(promptsResponse.data);
      setFilteredPrompts(promptsResponse.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load data');
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      } else if (error.response?.status === 403) {
        navigate('/pricing');
      }
    } finally {
      setLoading(false);
    }
  };

  const filterPrompts = () => {
    let filtered = prompts;

    if (selectedCategory !== 'All') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.title.toLowerCase().includes(term) ||
        p.content.toLowerCase().includes(term) ||
        p.category.toLowerCase().includes(term)
      );
    }

    setFilteredPrompts(filtered);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast.success('Logged out successfully');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: '#0a0a0a'}}>
        <p className="text-lg" style={{color: '#f59e0b'}}>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{background: '#0a0a0a'}}>
      {/* Header */}
      <header className="border-b border-border sticky top-0 z-10" style={{background: '#0a0a0a'}}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold" style={{fontFamily: 'Outfit', color: '#f59e0b'}}>AIPedia</h1>
          <div className="flex items-center gap-4">
            {user?.subscription_status === 'active' && (
              <Badge className="bg-primary text-primary-foreground" data-testid="pro-badge">
                <Crown className="w-3 h-3 mr-1" /> Pro
              </Badge>
            )}
            {user?.role === 'admin' && (
              <Button variant="outline" onClick={() => navigate('/admin')} data-testid="admin-panel-btn">
                Admin Panel
              </Button>
            )}
            <Button variant="ghost" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Search and Filter */}
        <div className="mb-8 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search prompts by title, category, or content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            <Filter className="w-5 h-5 text-muted-foreground flex-shrink-0" />
            {CATEGORIES.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                data-testid={`category-filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                className="whitespace-nowrap"
              >
                {category}
              </Button>
            ))}
          </div>
        </div>

        {/* Prompts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="prompts-grid">
          {filteredPrompts.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <p className="text-muted-foreground text-lg">No prompts found. Try adjusting your filters.</p>
            </div>
          ) : (
            filteredPrompts.map((prompt) => (
              <Card 
                key={prompt.id} 
                className="hover:border-primary/50 transition-colors duration-300 cursor-pointer"
                data-testid={`prompt-card-${prompt.id}`}
              >
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <Badge variant="outline" style={{borderColor: '#f59e0b', color: '#f59e0b'}}>
                      {prompt.category}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl" style={{fontFamily: 'Outfit'}}>{prompt.title}</CardTitle>
                  <CardDescription>
                    {new Date(prompt.created_at).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{prompt.content}</p>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
